import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, Camera, Scan, User, Gift, Clock, Coffee, Star, CreditCard, Ticket, CalendarDays, Phone, Users } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

// ═══════════════════════════════════════════════════════════
// SOUNDS
// ═══════════════════════════════════════════════════════════
const playSuccess = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523, 659, 784].forEach((freq, i) => {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.3);
      osc.start(ctx.currentTime + i * 0.12); osc.stop(ctx.currentTime + i * 0.12 + 0.3);
    });
    if (navigator.vibrate) navigator.vibrate([50, 30, 50]);
  } catch (e) {}
};

const playError = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [200, 150].forEach((freq, i) => {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "square"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, ctx.currentTime + i * 0.2);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.2 + 0.3);
      osc.start(ctx.currentTime + i * 0.2); osc.stop(ctx.currentTime + i * 0.2 + 0.3);
    });
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
  } catch (e) {}
};

const playScan = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sine"; osc.frequency.value = 1000;
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.start(); osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

// ═══════════════════════════════════════════════════════════
// STAFF PAGE
// ═══════════════════════════════════════════════════════════
const TABS = [
  { id: "coupon", label: "Gutschein", icon: Ticket },
  { id: "stamp", label: "Stempel", icon: Coffee },
  { id: "membership", label: "Member", icon: CreditCard },
  { id: "reservations", label: "Tische", icon: CalendarDays },
];

export default function StaffPage() {
  const [activeTab, setActiveTab] = useState("coupon");
  const [mode, setMode] = useState("scan");
  const [code, setCode] = useState("");
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staffUser, setStaffUser] = useState(null);
  const [staffToken, setStaffToken] = useState("");

  // On mount: clear everything and force fresh login
  useEffect(() => {
    const saved = localStorage.getItem("frankysfarm_staff_token");
    if (saved) {
      // Check if token is valid
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${saved}` } })
        .then(res => {
          if (res.data.role === "staff" || res.data.role === "admin" || res.data.role === "master-admin") {
            setStaffToken(saved);
            setStaffUser(res.data);
          } else {
            localStorage.removeItem("frankysfarm_staff_token");
          }
        })
        .catch(() => { localStorage.removeItem("frankysfarm_staff_token"); });
    }
  }, []);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [reservations, setReservations] = useState([]);
  const scannerRef = useRef(null);

  // Token check moved to mount effect above

  const handleStaffLogin = async () => {
    setLoginError("");
    console.log("Staff login attempt:", loginEmail);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail.trim(), password: loginPassword });
      const user = res.data.user;
      if (user.role !== "staff" && user.role !== "admin" && user.role !== "master-admin") {
        setLoginError("Nur Staff-Accounts erlaubt");
        return;
      }
      const token = res.data.access_token;
      localStorage.setItem("frankysfarm_staff_token", token);
      setStaffToken(token);
      setStaffUser(user);
      toast.success("Eingeloggt als " + user.name);
    } catch (e) {
      // Clear any bad tokens
      localStorage.removeItem("frankysfarm_staff_token");
      setStaffToken("");
      const detail = e.response?.data?.detail;
      if (typeof detail === "string") {
        setLoginError(detail);
      } else if (Array.isArray(detail)) {
        setLoginError("Bitte E-Mail und Passwort eingeben");
      } else {
        setLoginError("Login fehlgeschlagen");
      }
    }
  };

  const handleStaffLogout = () => {
    localStorage.removeItem("frankysfarm_staff_token");
    setStaffToken("");
    setStaffUser(null);
  };

  useEffect(() => {
    if (!staffUser) return;
    if (activeTab === "reservations") {
      fetchReservations();
    }
    // Scanner only starts when user clicks "Scannen" button
    return () => { try { scannerRef.current?.stop(); } catch(e) {} };
  }, [activeTab, staffUser]);

  const [scannerActive, setScannerActive] = useState(false);

  const startScanner = () => {
    setScannerActive(true);
    setTimeout(() => initScanner(), 200);
  };

  const stopScanner = () => {
    try { scannerRef.current?.stop(); } catch(e) {}
    setScannerActive(false);
  };

  const fetchReservations = async () => {
    try {
      const loc = staffUser?.location || "aachen";
      const res = await axios.get(`${API}/reservations/all?location=${loc}`, { headers: { Authorization: `Bearer ${staffToken}` } });
      setReservations(res.data || []);
    } catch (e) { console.log("Reservations error:", e); }
  };

  const updateReservationStatus = async (id, status) => {
    try {
      await axios.put(`${API}/reservations/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${staffToken}` } });
      playSuccess();
      toast.success(status === "confirmed" ? "Bestätigt!" : status === "cancelled" ? "Storniert" : "Aktualisiert");
      fetchReservations();
    } catch (e) {
      playError();
      toast.error("Fehler");
    }
  };

  const initScanner = () => {
    setTimeout(async () => {
      const el = document.getElementById("qr-reader");
      if (!el) return;
      try { await scannerRef.current?.stop(); } catch(e) {}
      
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      
      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 15, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
          (decodedText) => {
            // Pause scanner after successful scan
            // scanner stays active
            onScanSuccess(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.log("Camera error:", err);
        // Fallback: try any available camera
        try {
          const devices = await Html5Qrcode.getCameras();
          if (devices && devices.length > 0) {
            await scanner.start(
              devices[devices.length - 1].id,
              { fps: 15, qrbox: { width: 250, height: 250 } },
              (decodedText) => {
                // scanner stays active
                onScanSuccess(decodedText);
              },
              () => {}
            );
          }
        } catch (e2) {
          console.log("No camera available");
        }
      }
    }, 300);
  };

  const onScanSuccess = (decodedText) => {
    playScan();
    
    // STOP scanner immediately after scan
    try { scannerRef.current?.stop(); } catch(e) {}
    setScannerActive(false);
    
    // Strip prefixes
    let cleanCode = decodedText;
    if (cleanCode.includes("frankysfarm:member:")) cleanCode = cleanCode.split("frankysfarm:member:")[1];
    if (cleanCode.includes("frankysfarm:coupon:")) cleanCode = cleanCode.split("frankysfarm:coupon:")[1];
    // Neue URL-QRs (z.B. https://frankys-home.de/staff?code=FF-XXXXXX): code aus Querystring ziehen
    try {
      const u = new URL(cleanCode);
      const c = u.searchParams.get("code");
      if (c) cleanCode = c.trim();
    } catch (_) { /* kein URL-Format, cleanCode unverändert */ }

    setCode(cleanCode);
    // Auto-process based on tab
    if (activeTab === "coupon") handleCouponLookup(cleanCode);
    else if (activeTab === "stamp") handleStamp(cleanCode);
    else if (activeTab === "membership") handleMembershipLookup(cleanCode);
    else if (activeTab === "reservations") {} // no scan needed
    else handlePointsLookup(cleanCode);
  };

  const resetToScan = () => {
    setMode("scan");
    setCode("");
    setResultData(null);
    setScannerActive(false);
  };

  const token = staffToken;
  const headers = { Authorization: `Bearer ${token}` };

  // ═══ COUPON ═══
  const handleCouponLookup = async (couponCode) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/coupon/${couponCode}`);
      setResultData({ type: "coupon", ...res.data });
      setMode("result");
    } catch (e) {
      const isMemberCode = /^FF-[A-F0-9]{8}$/i.test(couponCode);
      // Member-Karte (FF- + 8 hex) wurde im Coupon-Modus gescannt:
      // statt Fehler → Mitglied nachschlagen und dessen offene Gutscheine direkt anbieten,
      // damit der Scan nie mehr fehlschlägt (egal ob Karten-QR oder Gutschein-QR gescannt wird).
      if (isMemberCode) {
        try {
          const m = await axios.get(`${API}/staff/member-coupons/${couponCode.toUpperCase()}`, { headers });
          const customer = m.data.customer;
          const coupons = m.data.coupons || [];
          if (coupons.length === 1) {
            playScan();
            setResultData({ type: "coupon", coupon: { ...coupons[0], status: "active" }, customer });
            setMode("result");
          } else if (coupons.length > 1) {
            playScan();
            setResultData({ type: "member_coupons", customer, coupons });
            setMode("result");
          } else {
            playError();
            toast.error(`${customer?.name || "Mitglied"} hat aktuell keine offenen Gutscheine.`);
          }
        } catch (me) {
          playError();
          toast.error("Mitglied nicht gefunden");
        }
        setLoading(false);
        return;
      }
      playError();
      toast.error("Gutschein nicht gefunden");
      // Owner per Mail benachrichtigen, dass der Scan fehlgeschlagen ist
      try {
        await axios.post(`${API}/staff/scan-failed`, {
          code: couponCode,
          reason: e.response?.data?.detail || "Coupon nicht gefunden",
          context: "staff_lookup_404",
        }, { headers });
      } catch (_) { /* still nicht blockieren */ }
    }
    setLoading(false);
  };

  // Löst einen Gutschein über seinen EIGENEN Code ein (nicht über den gescannten Code),
  // damit auch der Member-Karten-Weg sauber funktioniert.
  const redeemCouponByCode = async (couponCode) => {
    setLoading(true);
    try {
      await axios.post(`${API}/staff/redeem`, { code: (couponCode || "").toUpperCase() }, { headers });
      playSuccess();
      toast.success("Gutschein eingelöst!");
      setResultData(prev => ({ ...prev, type: "coupon", redeemed: true }));
    } catch (e) {
      playError();
      const detail = e.response?.data?.detail || "Fehler beim Einlösen";
      toast.error(detail);
      // Owner-Mail bei fehlgeschlagenem Einlösen (bereits eingelöst / abgelaufen / etc.)
      try {
        await axios.post(`${API}/staff/scan-failed`, {
          code: (couponCode || "").toUpperCase(),
          reason: detail,
          context: "staff_redeem_failed",
        }, { headers });
      } catch (_) { /* still */ }
    }
    setLoading(false);
  };

  const handleRedeemCoupon = () => redeemCouponByCode(resultData?.coupon?.code || code);

  // ═══ STAMP ═══ - First lookup customer, then show options
  const handleStamp = async (memberCode) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff/customer/${memberCode}`, { headers: { Authorization: `Bearer ${staffToken}` } });
      playScan();
      setResultData({ type: "stamp_options", ...res.data, memberCode });
      setMode("result");
    } catch (e) {
      playError();
      toast.error("Mitglied nicht gefunden");
    }
    setLoading(false);
  };

  const doGiveStamp = async (memberCode) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/staff/stamp`, { member_code: memberCode }, { headers: { Authorization: `Bearer ${staffToken}` } });
      if (res.data.success === false) {
        playError();
        toast.error(res.data.message);
        setResultData(prev => ({ ...prev, stampResult: { error: true, message: res.data.message } }));
      } else {
        playSuccess();
        toast.success("Stempel vergeben!");
        setResultData(prev => ({ ...prev, stampResult: res.data }));
      }
    } catch (e) {
      playError();
      toast.error(e.response?.data?.detail || "Fehler");
    }
    setLoading(false);
  };

  const doRedeemMemberDrink = async (userId, drinkName) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/membership/redeem`, {
        user_id: userId, redemption_type: "drink", drink_name: drinkName
      }, { headers: { Authorization: `Bearer ${staffToken}` } });
      playSuccess();
      toast.success("Eingelöst – Hinweise beachten!");
      setResultData(prev => ({
        ...prev,
        drinkRedeemed: drinkName,
        staffNotice: {
          title: res.data.staff_notice_title,
          included: res.data.staff_notice_included || [],
          extra_charge: res.data.staff_notice_extra_charge || [],
          allowed_drinks: res.data.allowed_drinks || [],
          size_note: res.data.size_note,
          included_milk: res.data.included_milk,
          discount_percent: res.data.discount_percent || 0,
          remaining: res.data.remaining_drinks,
          plan_id: res.data.plan_id,
        },
      }));
    } catch (e) {
      playError();
      toast.error(e.response?.data?.detail || "Fehler");
    }
    setLoading(false);
  };

  // ═══ MEMBERSHIP ═══
  const handleMembershipLookup = async (memberCode) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff/customer/${memberCode}`, { headers });
      playScan();
      setResultData({ type: "membership", ...res.data });
      setMode("result");
    } catch (e) {
      playError();
      toast.error("Mitglied nicht gefunden");
    }
    setLoading(false);
  };

  const handleRedeemDrink = async (userId, drinkName) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/membership/redeem`, {
        user_id: userId, redemption_type: "drink", drink_name: drinkName || "Getränk"
      }, { headers });
      playSuccess();
      toast.success("Eingelöst – bitte Hinweise beachten!");
      // Response enthaelt staff_notice_* Felder -> oeffne Staff-Notice Dialog
      setResultData(prev => ({
        ...prev,
        drinkRedeemed: drinkName,
        staffNotice: {
          title: res.data.staff_notice_title,
          included: res.data.staff_notice_included || [],
          extra_charge: res.data.staff_notice_extra_charge || [],
          allowed_drinks: res.data.allowed_drinks || [],
          size_note: res.data.size_note,
          included_milk: res.data.included_milk,
          discount_percent: res.data.discount_percent || 0,
          remaining: res.data.remaining_drinks,
          plan_id: res.data.plan_id,
        },
      }));
    } catch (e) {
      playError();
      toast.error(e.response?.data?.detail || "Fehler beim Einlösen");
    }
    setLoading(false);
  };

  // ═══ POINTS ═══
  const handlePointsLookup = async (memberCode) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/member/${memberCode}`);
      setResultData({ type: "points", user: res.data });
      setMode("result");
    } catch (e) {
      playError();
      toast.error("Mitglied nicht gefunden");
    }
    setLoading(false);
  };

  const handleAddPoints = async (userId, points, desc) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/member/${code}`);
      await axios.post(`${API}/points/add`, {
        member_code: code, points, description: desc || "Punkte vom Staff"
      }, { headers });
      playSuccess();
      toast.success(`+${points} Punkte vergeben!`);
      setResultData(prev => ({ ...prev, pointsAdded: points }));
    } catch (e) {
      playError();
      toast.error(e.response?.data?.detail || "Fehler");
    }
    setLoading(false);
  };


  // Show login if no staff user
  if (!staffUser) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: C.bg }}>
        <div className="w-full max-w-sm p-6 rounded-2xl" style={{ backgroundColor: "#fff" }}>
          <div className="text-center mb-6">
            <img src="/logo-frankysfarm.png" alt="Logo" className="w-16 h-16 mx-auto rounded-xl mb-3" />
            <h1 className="text-xl font-bold" style={{ color: C.green, fontFamily: "'Space Grotesk', sans-serif" }}>Staff Login</h1>
          </div>
          {loginError && <p className="text-sm text-center mb-3" style={{ color: "#C25D3D" }}>{loginError}</p>}
          <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
            placeholder="Staff E-Mail" className="w-full px-4 py-3 rounded-xl mb-3 text-sm"
            style={{ backgroundColor: C.bg, border: `2px solid ${C.border || "#E6DED3"}` }} />
          <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
            placeholder="Passwort" className="w-full px-4 py-3 rounded-xl mb-4 text-sm"
            style={{ backgroundColor: C.bg, border: `2px solid ${C.border || "#E6DED3"}` }}
            onKeyDown={e => { if (e.key === "Enter") handleStaffLogin(); }} />
          <button onClick={handleStaffLogin}
            className="w-full py-3 rounded-xl text-white font-bold"
            style={{ backgroundColor: C.green }}>Anmelden</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.bg }}>
      {/* Header */}
      <div className="p-4 text-center" style={{ background: `linear-gradient(135deg, ${C.green}, #1E3D2A)` }}>
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Franky's Farm Staff
        </h1>
        <div className="flex items-center justify-center gap-3 mt-1"><p className="text-white/60 text-xs">{staffUser?.name} | {staffUser?.location}</p><button onClick={handleStaffLogout} className="text-white/40 text-xs underline">Logout</button></div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-2 mx-3 mt-3 rounded-2xl" style={{ backgroundColor: C.bgCard || "#fff" }}>
        {TABS.map(tab => (
          <button key={tab.id}
            onClick={() => { setActiveTab(tab.id); resetToScan(); }}
            className="flex-1 py-2.5 rounded-xl flex flex-col items-center gap-1 transition-all"
            style={{
              backgroundColor: activeTab === tab.id ? C.green : "transparent",
              color: activeTab === tab.id ? "#fff" : C.textMuted || "#6B5B4F",
            }}>
            <tab.icon size={18} />
            <span className="text-[10px] font-bold">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        <AnimatePresence mode="wait">
          {activeTab === "reservations" ? (
            <motion.div key="reservations" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold" style={{ color: C.text || "#1A1A1A", fontFamily: "'Space Grotesk', sans-serif" }}>Reservierungen</h2>
                <button onClick={fetchReservations} className="text-sm font-semibold" style={{ color: C.green }}>Aktualisieren</button>
              </div>
              {reservations.length === 0 ? (
                <div className="text-center py-8 rounded-2xl" style={{ backgroundColor: "#fff" }}>
                  <CalendarDays size={48} className="mx-auto mb-3" style={{ color: C.textMuted || "#6B5B4F" }} />
                  <p className="font-bold" style={{ color: C.textMuted || "#6B5B4F" }}>Keine Reservierungen</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reservations.map((r) => (
                    <div key={r.id} className="p-4 rounded-2xl" style={{ backgroundColor: "#fff" }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold" style={{ color: C.text || "#1A1A1A", fontFamily: "'Space Grotesk', sans-serif" }}>
                          {r.date} um {r.time}
                        </span>
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{
                          backgroundColor: r.status === "confirmed" ? C.green + "15" : r.status === "cancelled" ? "#C25D3D15" : (C.orange || "#E07A3D") + "15",
                          color: r.status === "confirmed" ? C.green : r.status === "cancelled" ? "#C25D3D" : (C.orange || "#E07A3D"),
                        }}>
                          {r.status === "confirmed" ? "Bestätigt" : r.status === "cancelled" ? "Storniert" : "Offen"}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <User size={14} style={{ color: C.textMuted || "#6B5B4F" }} />
                          <span className="text-sm" style={{ color: C.text || "#1A1A1A" }}>{r.name || "Unbekannt"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users size={14} style={{ color: C.textMuted || "#6B5B4F" }} />
                          <span className="text-sm" style={{ color: C.text || "#1A1A1A" }}>{r.guests} Personen</span>
                        </div>
                        {r.phone && (
                          <div className="flex items-center gap-2">
                            <Phone size={14} style={{ color: C.textMuted || "#6B5B4F" }} />
                            <a href={"tel:" + r.phone} className="text-sm font-semibold" style={{ color: C.green }}>{r.phone}</a>
                          </div>
                        )}
                        {r.email && (
                          <p className="text-xs" style={{ color: C.textMuted || "#6B5B4F" }}>{r.email}</p>
                        )}
                        {r.notes && (
                          <p className="text-xs mt-1 italic" style={{ color: C.textMuted || "#6B5B4F" }}>"{r.notes}"</p>
                        )}
                      </div>
                      {r.status === "pending" && (
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => updateReservationStatus(r.id, "confirmed")}
                            className="flex-1 py-2 rounded-xl text-white text-sm font-bold" style={{ backgroundColor: C.green }}>
                            Bestätigen
                          </button>
                          <button onClick={() => updateReservationStatus(r.id, "cancelled")}
                            className="flex-1 py-2 rounded-xl text-sm font-bold" style={{ backgroundColor: "#C25D3D15", color: "#C25D3D" }}>
                            Stornieren
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : mode === "scan" ? (
            <motion.div key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Scanner - only active when clicked */}
              {scannerActive ? (
                <div className="relative">
                  <div className="rounded-2xl overflow-hidden mb-4" style={{ backgroundColor: "#000" }}>
                    <div id="qr-reader" style={{ width: "100%" }} />
                  </div>
                  <button onClick={stopScanner}
                    className="absolute top-2 right-2 px-3 py-1.5 rounded-xl text-xs font-bold text-white"
                    style={{ backgroundColor: "#C25D3D", zIndex: 10 }}>
                    Scanner stoppen
                  </button>
                </div>
              ) : (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={startScanner}
                  className="w-full py-6 rounded-2xl flex flex-col items-center gap-3 mb-4"
                  style={{ backgroundColor: C.green, boxShadow: "0 8px 25px " + C.green + "40" }}>
                  <Camera size={40} color="#fff" />
                  <span className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>QR-Code scannen</span>
                </motion.button>
              )}

              {/* Manual code input */}
              <div className="flex gap-2">
                <input
                  type="text" value={code} onChange={e => setCode(e.target.value)}
                  placeholder={
                    activeTab === "coupon" ? "Gutschein-Code (z.B. FF-ABC123)" :
                    activeTab === "stamp" ? "Member-Code (z.B. FF-12345678)" :
                    "Member-Code eingeben"
                  }
                  className="flex-1 px-4 py-3 rounded-xl text-sm font-medium"
                  style={{ backgroundColor: "#fff", border: `2px solid ${C.border || "#E6DED3"}`, fontFamily: "'Figtree', sans-serif" }}
                  onKeyDown={e => { if (e.key === "Enter" && code) onScanSuccess(code); }}
                />
                <button
                  onClick={() => code && onScanSuccess(code)}
                  disabled={!code || loading}
                  className="px-5 py-3 rounded-xl text-sm font-bold text-white"
                  style={{ backgroundColor: code ? C.green : (C.border || "#E6DED3") }}>
                  {loading ? "..." : "OK"}
                </button>
              </div>

              <p className="text-center text-xs mt-3" style={{ color: C.textMuted || "#6B5B4F", fontFamily: "'Figtree', sans-serif" }}>
                {activeTab === "coupon" && "QR-Code des Gutscheins scannen oder Code eingeben"}
                {activeTab === "stamp" && "Member-QR-Code scannen fuer Stempel"}
                {activeTab === "membership" && "Member-QR-Code scannen fuer Membership-Getränk"}
                {activeTab === "points" && "Member-QR-Code scannen fuer Punkte vergeben"}
              </p>
            </motion.div>
          ) : (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {/* ═══ COUPON RESULT ═══ */}
              {resultData?.type === "coupon" && (
                <div className="p-5 rounded-2xl text-center" style={{ backgroundColor: "#fff" }}>
                  {resultData.redeemed ? (
                    <>
                      <CheckCircle size={64} className="mx-auto mb-3" style={{ color: C.green }} />
                      <h2 className="text-xl font-bold" style={{ color: C.green, fontFamily: "'Space Grotesk', sans-serif" }}>Eingelöst!</h2>
                    </>
                  ) : resultData.coupon?.status === "redeemed" ? (
                    <>
                      <XCircle size={64} className="mx-auto mb-3" style={{ color: "#C25D3D" }} />
                      <h2 className="text-xl font-bold" style={{ color: "#C25D3D", fontFamily: "'Space Grotesk', sans-serif" }}>Bereits eingelöst</h2>
                    </>
                  ) : resultData.coupon?.status === "expired" ? (
                    <>
                      <AlertTriangle size={64} className="mx-auto mb-3" style={{ color: C.orange || "#E07A3D" }} />
                      <h2 className="text-xl font-bold" style={{ color: C.orange || "#E07A3D", fontFamily: "'Space Grotesk', sans-serif" }}>Abgelaufen</h2>
                    </>
                  ) : (
                    <>
                      <Gift size={64} className="mx-auto mb-3" style={{ color: C.green }} />
                      <h2 className="text-xl font-bold" style={{ color: C.text || "#1A1A1A", fontFamily: "'Space Grotesk', sans-serif" }}>{resultData.coupon?.title}</h2>
                      <p className="text-sm mt-1" style={{ color: C.textMuted || "#6B5B4F" }}>{resultData.coupon?.value}</p>
                      {resultData.customer && (
                        <p className="text-sm mt-2 font-semibold" style={{ color: C.green }}>Kunde: {resultData.customer.name}</p>
                      )}
                      <button onClick={handleRedeemCoupon} disabled={loading}
                        className="w-full mt-4 py-4 rounded-2xl text-white font-bold text-base"
                        style={{ backgroundColor: C.green, fontFamily: "'Space Grotesk', sans-serif" }}>
                        {loading ? "Wird eingelöst..." : "Jetzt einlösen"}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* ═══ MEMBER-KARTE im Coupon-Modus → offene Gutscheine zur Auswahl ═══ */}
              {resultData?.type === "member_coupons" && (
                <div className="p-5 rounded-2xl" style={{ backgroundColor: "#fff" }}>
                  <div className="text-center mb-4">
                    <Gift size={40} className="mx-auto mb-2" style={{ color: C.green }} />
                    <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{resultData.customer?.name}</h2>
                    <p className="text-sm" style={{ color: C.textMuted || "#6B5B4F" }}>Offene Gutscheine — bitte auswählen</p>
                  </div>
                  <div className="space-y-2">
                    {resultData.coupons.map((c) => (
                      <button key={c.code} onClick={() => redeemCouponByCode(c.code)} disabled={loading}
                        className="w-full p-4 rounded-2xl text-left flex items-center justify-between"
                        style={{ backgroundColor: C.green + "12", border: `1px solid ${C.green}33` }}>
                        <span>
                          <span className="block font-bold" style={{ color: C.text || "#1A1A1A" }}>{c.title}</span>
                          {c.value && <span className="block text-sm" style={{ color: C.textMuted || "#6B5B4F" }}>{c.value}</span>}
                        </span>
                        <span className="font-bold text-sm" style={{ color: C.green }}>{loading ? "..." : "Einlösen"}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ═══ STAMP OPTIONS - Show customer + action buttons ═══ */}
              {resultData?.type === "stamp_options" && (
                <div className="p-5 rounded-2xl" style={{ backgroundColor: "#fff" }}>
                  {/* Customer Info */}
                  <div className="text-center mb-4">
                    <User size={40} className="mx-auto mb-2" style={{ color: C.green }} />
                    <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{resultData.customer?.name}</h2>
                    <p className="text-sm" style={{ color: C.textMuted || "#6B5B4F" }}>
                      {resultData.customer?.stamps || 0}/10 Stempel | {resultData.customer?.points || 0} Punkte
                    </p>
                  </div>

                  {/* Stamp result if already given */}
                  {resultData.stampResult ? (
                    <div className="text-center mb-4 p-3 rounded-xl" style={{ backgroundColor: resultData.stampResult.error ? "#C25D3D12" : C.green + "12" }}>
                      {resultData.stampResult.error ? (
                        <p className="font-bold" style={{ color: "#C25D3D" }}>{resultData.stampResult.message}</p>
                      ) : (
                        <>
                          <CheckCircle size={32} className="mx-auto mb-1" style={{ color: C.green }} />
                          <p className="font-bold" style={{ color: C.green }}>Stempel vergeben! ({resultData.stampResult.total_stamps}/10)</p>
                        </>
                      )}
                    </div>
                  ) : null}

                  {/* Drink redeemed + Staff-Notice mit ALLEN Hinweisen */}
                  {resultData.drinkRedeemed && resultData.staffNotice && (
                    <div className="mb-4 rounded-2xl overflow-hidden border-2" style={{ borderColor: C.green }}>
                      <div className="p-4 text-center" style={{ backgroundColor: C.green, color: "#fff" }}>
                        <CheckCircle size={40} className="mx-auto mb-1" />
                        <p className="text-lg font-extrabold">{resultData.staffNotice.title || "Eingelöst!"}</p>
                        <p className="text-xs opacity-90 mt-0.5">
                          {resultData.staffNotice.remaining !== undefined && resultData.staffNotice.remaining !== null
                            ? `Noch ${resultData.staffNotice.remaining} diese Periode übrig`
                            : "Eingelöst"}
                        </p>
                      </div>

                      {/* INKLUSIVE-Block */}
                      {resultData.staffNotice.included?.length > 0 && (
                        <div className="p-4" style={{ backgroundColor: C.green + "12" }}>
                          <p className="text-xs font-extrabold uppercase tracking-wider mb-2" style={{ color: C.green }}>
                            ✅ Inklusive (gratis für Mitglied)
                          </p>
                          <ul className="space-y-1">
                            {resultData.staffNotice.included.map((item, i) => (
                              <li key={i} className="text-sm flex items-start gap-2" style={{ color: C.text || "#1A1A1A" }}>
                                <span style={{ color: C.green }}>•</span><span>{item}</span>
                              </li>
                            ))}
                          </ul>
                          {resultData.staffNotice.discount_percent > 0 && (
                            <p className="text-xs font-bold mt-2 p-2 rounded" style={{ backgroundColor: C.green + "20", color: C.green }}>
                              💰 {resultData.staffNotice.discount_percent}% Rabatt auf die gesamte Bestellung!
                            </p>
                          )}
                        </div>
                      )}

                      {/* EXTRA-KASSIEREN-Block - fett und rot */}
                      {resultData.staffNotice.extra_charge?.length > 0 && (
                        <div className="p-4 border-t-2" style={{ backgroundColor: "#FFF3E0", borderColor: "#E07A3D" }}>
                          <p className="text-xs font-extrabold uppercase tracking-wider mb-2" style={{ color: "#C25D3D" }}>
                            ⚠️ Zusätzlich kassieren!
                          </p>
                          <ul className="space-y-1">
                            {resultData.staffNotice.extra_charge.map((item, i) => (
                              <li key={i} className="text-sm font-semibold flex items-start gap-2" style={{ color: "#8B3A1F" }}>
                                <span>→</span><span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fallback fuer alte Responses ohne staff_notice */}
                  {resultData.drinkRedeemed && !resultData.staffNotice && (
                    <div className="text-center mb-4 p-3 rounded-xl" style={{ backgroundColor: C.green + "12" }}>
                      <CheckCircle size={32} className="mx-auto mb-1" style={{ color: C.green }} />
                      <p className="font-bold" style={{ color: C.green }}>Getränk eingelöst!</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {!resultData.stampResult && !resultData.drinkRedeemed && (
                    <div className="space-y-3">
                      {/* Stamp Button */}
                      <button onClick={() => doGiveStamp(resultData.memberCode)} disabled={loading}
                        className="w-full py-4 rounded-xl text-white font-bold text-base flex items-center justify-center gap-3"
                        style={{ backgroundColor: C.green, fontFamily: "'Space Grotesk', sans-serif" }}>
                        <Coffee size={20} /> {loading ? "..." : "Stempel geben"}
                      </button>

                      {/* Membership Drinks - show ALL active memberships */}
                      {resultData.memberships && resultData.memberships.length > 0 ? (
                        resultData.memberships.map((mem, idx) => {
                          const drinkLabel = mem.drink_type === "matcha" ? "Matcha Latte" : "Kaffee";
                          const wasJustRedeemed = resultData.drinkRedeemed === drinkLabel;
                          const planColor = mem.drink_type === "matcha" ? "#4A7C59" : (C.orange || "#E07A3D");
                          return (
                          <div key={idx} className="mb-3 rounded-xl overflow-hidden border" style={{ borderColor: planColor + "40" }}>
                            {/* Plan-Header mit erlaubten Drinks */}
                            <div className="p-3" style={{ backgroundColor: planColor + "12" }}>
                              <p className="text-xs font-extrabold uppercase tracking-wider" style={{ color: planColor }}>
                                {mem.plan_name}
                              </p>
                              {mem.allowed_drinks?.length > 0 && (
                                <p className="text-xs mt-1" style={{ color: C.text || "#1A1A1A" }}>
                                  <b>Erlaubt:</b> {mem.allowed_drinks.join(" · ")}
                                </p>
                              )}
                              {mem.size_note && (
                                <p className="text-xs" style={{ color: C.textMuted || "#6B5B4F" }}>{mem.size_note}</p>
                              )}
                              {mem.discount_percent > 0 && (
                                <p className="text-xs mt-1 font-bold" style={{ color: planColor }}>
                                  + {mem.discount_percent}% Rabatt auf gesamte Bestellung
                                </p>
                              )}
                            </div>
                            <div className="p-2">
                            {wasJustRedeemed ? (
                              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: C.green + "12" }}>
                                <CheckCircle size={20} className="mx-auto mb-1" style={{ color: C.green }} />
                                <p className="text-sm font-bold" style={{ color: C.green }}>{drinkLabel} eingelöst!</p>
                              </div>
                            ) : mem.paused ? (
                              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: "#FFE5E5" }}>
                                <p className="text-sm font-bold" style={{ color: "#C25D3D" }}>
                                  ⚠️ Abo pausiert (Zahlung fehlgeschlagen) – bitte Kunde informieren
                                </p>
                              </div>
                            ) : mem.remaining_drinks > 0 ? (
                              <button onClick={() => doRedeemMemberDrink(resultData.customer.id, drinkLabel)}
                                disabled={loading}
                                className="w-full py-4 rounded-xl text-white font-bold text-base flex items-center justify-center gap-3"
                                style={{ backgroundColor: planColor, fontFamily: "'Space Grotesk', sans-serif" }}>
                                <Star size={20} /> {loading ? "..." : mem.drink_type === "matcha" ? "Wochen-Matcha einlösen" : "Tages-Kaffee einlösen"}
                              </button>
                            ) : (
                              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: planColor + "12" }}>
                                <p className="text-sm font-bold" style={{ color: planColor }}>
                                  {mem.period === "week" ? "Diese Woche schon eingelöst" : "Heute schon eingelöst"}
                                </p>
                              </div>
                            )}
                            </div>
                          </div>
                          );
                        })
                      ) : !resultData.membership ? (
                        <div className="p-3 rounded-xl text-center" style={{ backgroundColor: C.bg }}>
                          <p className="text-xs" style={{ color: C.textMuted || "#6B5B4F" }}>Kein Membership-Abo aktiv</p>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

              {/* ═══ MEMBERSHIP RESULT ═══ */}
              {resultData?.type === "membership" && (
                <div className="p-5 rounded-2xl" style={{ backgroundColor: "#fff" }}>
                  <div className="text-center mb-4">
                    <User size={48} className="mx-auto mb-2" style={{ color: C.green }} />
                    <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{resultData.user?.name}</h2>
                    <p className="text-sm" style={{ color: C.textMuted || "#6B5B4F" }}>{resultData.user?.email}</p>
                  </div>
                  {resultData.memberships && resultData.memberships.length > 0 ? (
                    <div className="space-y-3">
                      {resultData.memberships.map((mem, idx) => {
                        const drinkLabel = mem.drink_type === "matcha" ? "Matcha Latte" : "Kaffee";
                        const wasRedeemed = resultData.drinkRedeemed === drinkLabel;
                        return (
                          <div key={idx} className="p-4 rounded-xl" style={{ backgroundColor: mem.drink_type === "matcha" ? "#4A7C5912" : (C.orange || "#E07A3D") + "12" }}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-bold" style={{ color: mem.drink_type === "matcha" ? "#4A7C59" : (C.orange || "#E07A3D"), fontFamily: "'Space Grotesk', sans-serif" }}>
                                {mem.plan_name}
                              </p>
                              <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ 
                                backgroundColor: mem.remaining_drinks > 0 ? C.green + "15" : "#C25D3D15",
                                color: mem.remaining_drinks > 0 ? C.green : "#C25D3D" 
                              }}>
                                {mem.remaining_drinks > 0 ? "Verfügbar" : "Eingelöst"}
                              </span>
                            </div>
                            <p className="text-xs mb-2" style={{ color: C.textMuted || "#6B5B4F" }}>
                              {mem.period === "week" ? `${mem.used_this_period}/1 diese Woche` : `${mem.used_this_period}/1 heute`}
                            </p>
                            {wasRedeemed ? (
                              <div className="p-2 rounded-lg text-center" style={{ backgroundColor: C.green + "15" }}>
                                <p className="text-sm font-bold" style={{ color: C.green }}>✓ {drinkLabel} eingelöst!</p>
                              </div>
                            ) : mem.remaining_drinks > 0 ? (
                              <button onClick={() => handleRedeemDrink(resultData.customer.id, drinkLabel)}
                                disabled={loading}
                                className="w-full py-3 rounded-xl text-white font-bold text-sm"
                                style={{ backgroundColor: mem.drink_type === "matcha" ? "#4A7C59" : (C.orange || "#E07A3D") }}>
                                {loading ? "..." : `${drinkLabel} einlösen`}
                              </button>
                            ) : (
                              <p className="text-xs text-center font-semibold" style={{ color: "#C25D3D" }}>
                                {mem.period === "week" ? "Nächste Woche wieder" : "Morgen wieder"}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl text-center" style={{ backgroundColor: "#C25D3D12" }}>
                      <p className="font-bold" style={{ color: "#C25D3D" }}>Kein aktives Membership</p>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ POINTS RESULT ═══ */}
              {resultData?.type === "points" && (
                <div className="p-5 rounded-2xl" style={{ backgroundColor: "#fff" }}>
                  <div className="text-center mb-4">
                    <Star size={48} className="mx-auto mb-2" style={{ color: C.gold || "#D4A574" }} />
                    <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{resultData.user?.name}</h2>
                    <p className="text-3xl font-black mt-1" style={{ color: C.green }}>{resultData.user?.points || 0} Punkte</p>
                  </div>
                  {resultData.pointsAdded ? (
                    <div className="text-center p-3 rounded-xl" style={{ backgroundColor: C.green + "12" }}>
                      <CheckCircle size={32} className="mx-auto mb-1" style={{ color: C.green }} />
                      <p className="font-bold" style={{ color: C.green }}>+{resultData.pointsAdded} Punkte vergeben!</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {[10, 25, 50].map(pts => (
                        <button key={pts} onClick={() => handleAddPoints(resultData.user?.id, pts, `+${pts} Punkte`)}
                          disabled={loading}
                          className="py-3 rounded-xl text-white font-bold"
                          style={{ backgroundColor: C.green }}>
                          +{pts}
                        </button>
                      ))}
                      {[100, 200, 500].map(pts => (
                        <button key={pts} onClick={() => handleAddPoints(resultData.user?.id, pts, `+${pts} Punkte`)}
                          disabled={loading}
                          className="py-3 rounded-xl text-white font-bold"
                          style={{ backgroundColor: C.orange || "#E07A3D" }}>
                          +{pts}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Back Button */}
              <button onClick={resetToScan}
                className="w-full mt-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                style={{ backgroundColor: C.bg, color: C.text || "#1A1A1A", fontFamily: "'Space Grotesk', sans-serif" }}>
                <Scan size={18} /> Nächsten scannen
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
